import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Clock, 
  Package,
  Phone, 
  Mail, 
  AlertCircle,
  Info,
  Truck,
  Search
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../mock';

// TCV Vials data from Chinese catalog
const tcvVials = [
  {
    id: "TCV-10",
    name: "10 DRAM Thumb Tab Vials with Reversible Caps",
    capacity: "10 DRAMS (1.25 OZ, 36 ML)",
    unitsPerCase: 260,
    grossWeight: "3.12 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant one-click closure", "EZ Open screw-on cap", "Reversible", "Customizable colors"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.28\"",
    height: "2.75\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png"
  },
  {
    id: "TCV-13",
    name: "13 DRAM Thumb Tab Vials with Reversible Caps",
    capacity: "13 DRAMS (1.63 OZ, 48 ML)",
    unitsPerCase: 215,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant one-click closure", "EZ Open screw-on cap", "Reversible", "Customizable colors"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.53\"",
    height: "2.56\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png"
  },
  {
    id: "TCV-16",
    name: "16 DRAM Thumb Tab Vials with Reversible Caps",
    capacity: "16 DRAMS (2.0 OZ, 58 ML)",
    unitsPerCase: 175,
    grossWeight: "3.4 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant one-click closure", "EZ Open screw-on cap", "Reversible", "Customizable colors"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.53\"",
    height: "3.03\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png"
  },
  {
    id: "TCV-20",
    name: "20 DRAM Thumb Tab Vials with Reversible Caps",
    capacity: "20 DRAMS (2.5 OZ, 73 ML)",
    unitsPerCase: 160,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant one-click closure", "EZ Open screw-on cap", "Reversible", "Customizable colors"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.53\"",
    height: "3.50\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png"
  },
  {
    id: "TCV-30",
    name: "30 DRAM Thumb Tab Vials with Reversible Caps",
    capacity: "30 DRAMS (3.75 OZ, 110 ML)",
    unitsPerCase: 126,
    grossWeight: "3.35 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant one-click closure", "EZ Open screw-on cap", "Reversible", "Customizable colors"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "3.03\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png"
  },
  {
    id: "TCV-40",
    name: "40 DRAM Thumb Tab Vials with Reversible Caps",
    capacity: "40 DRAMS (5.0 OZ, 146 ML)",
    unitsPerCase: 105,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant one-click closure", "EZ Open screw-on cap", "Reversible", "Customizable colors"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "3.78\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png"
  },
  {
    id: "TCV-60",
    name: "60 DRAM Thumb Tab Vials with Reversible Caps",
    capacity: "60 DRAMS (7.5 OZ, 220 ML)",
    unitsPerCase: 78,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant one-click closure", "EZ Open screw-on cap", "Reversible", "Customizable colors"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "5.31\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png"
  }
];

// Snap Cap Vials data
const snapCapVials = [
  {
    id: "SC-06",
    name: "6 DRAM Snap Cap Vials",
    capacity: "6 DRAMS (0.75 OZ, 21 ML)",
    unitsPerCase: 280,
    grossWeight: "2.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Non-locking snap caps", "Easy access", "NOT child resistant", "Quick opening/closing"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "0.94\"",
    height: "2.25\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png"
  },
  {
    id: "SC-08",
    name: "8 DRAM Snap Cap Vials",
    capacity: "8 DRAMS (1.0 OZ, 29 ML)",
    unitsPerCase: 240,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Non-locking snap caps", "Easy access", "NOT child resistant", "Quick opening/closing"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.00\"",
    height: "2.50\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png"
  },
  {
    id: "SC-13",
    name: "13 DRAM Snap Cap Vials",
    capacity: "13 DRAMS (1.63 OZ, 48 ML)",
    unitsPerCase: 200,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Non-locking snap caps", "Easy access", "NOT child resistant", "Quick opening/closing"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "2.75\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png"
  },
  {
    id: "SC-16",
    name: "16 DRAM Snap Cap Vials",
    capacity: "16 DRAMS (2.0 OZ, 58 ML)",
    unitsPerCase: 160,
    grossWeight: "3.1 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Non-locking snap caps", "Easy access", "NOT child resistant", "Quick opening/closing"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "3.25\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png"
  },
  {
    id: "SC-20",
    name: "20 DRAM Snap Cap Vials",
    capacity: "20 DRAMS (2.5 OZ, 73 ML)",
    unitsPerCase: 140,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Non-locking snap caps", "Easy access", "NOT child resistant", "Quick opening/closing"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.77\"",
    height: "3.0\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png"
  },
  {
    id: "SC-30",
    name: "30 DRAM Snap Cap Vials",
    capacity: "30 DRAMS (3.75 OZ, 110 ML)",
    unitsPerCase: 110,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Non-locking snap caps", "Easy access", "NOT child resistant", "Quick opening/closing"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "3.5\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png"
  },
  {
    id: "SC-40",
    name: "40 DRAM Snap Cap Vials",
    capacity: "40 DRAMS (5.0 OZ, 146 ML)",
    unitsPerCase: 95,
    grossWeight: "3.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Non-locking snap caps", "Easy access", "NOT child resistant", "Quick opening/closing"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "4.25\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png"
  }
];

// Child-Resistant Push & Turn Vials data
const crVials = [
  {
    id: "CR-05",
    name: "5 DRAM Child-Resistant Push & Turn Vials",
    capacity: "5 DRAMS (0.63 OZ, 18 ML)",
    unitsPerCase: 320,
    grossWeight: "2.6 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "0.88\"",
    height: "2.13\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-06",
    name: "6 DRAM Child-Resistant Push & Turn Vials",
    capacity: "6 DRAMS (0.75 OZ, 21 ML)",
    unitsPerCase: 280,
    grossWeight: "2.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "0.94\"",
    height: "2.25\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-08",
    name: "8 DRAM Child-Resistant Push & Turn Vials",
    capacity: "8 DRAMS (1.0 OZ, 29 ML)",
    unitsPerCase: 240,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.00\"",
    height: "2.50\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-13",
    name: "13 DRAM Child-Resistant Push & Turn Vials",
    capacity: "13 DRAMS (1.63 OZ, 48 ML)",
    unitsPerCase: 200,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "2.75\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-16",
    name: "16 DRAM Child-Resistant Push & Turn Vials",
    capacity: "16 DRAMS (2.0 OZ, 58 ML)",
    unitsPerCase: 160,
    grossWeight: "3.1 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "3.25\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-20",
    name: "20 DRAM Child-Resistant Push & Turn Vials",
    capacity: "20 DRAMS (2.5 OZ, 73 ML)",
    unitsPerCase: 140,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.77\"",
    height: "3.0\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-30",
    name: "30 DRAM Child-Resistant Push & Turn Vials",
    capacity: "30 DRAMS (3.75 OZ, 110 ML)",
    unitsPerCase: 110,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "3.5\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-40",
    name: "40 DRAM Child-Resistant Push & Turn Vials",
    capacity: "40 DRAMS (5.0 OZ, 146 ML)",
    unitsPerCase: 95,
    grossWeight: "3.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "4.25\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  },
  {
    id: "CR-60",
    name: "60 DRAM Child-Resistant Push & Turn Vials",
    capacity: "60 DRAMS (7.5 OZ, 220 ML)",
    unitsPerCase: 78,
    grossWeight: "4.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant push & turn", "Screen printing available", "Cap customization", "+5% additional snap caps"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "5.31\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png"
  }
];

// Reversible Cap Vials data
const rcvVials = [
  {
    id: "RCV-06",
    name: "6 DRAM Reversible Cap Vials",
    capacity: "6 DRAMS (0.75 OZ, 21 ML)",
    unitsPerCase: 280,
    grossWeight: "2.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "0.94\"",
    height: "2.25\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  },
  {
    id: "RCV-08",
    name: "8 DRAM Reversible Cap Vials",
    capacity: "8 DRAMS (1.0 OZ, 29 ML)",
    unitsPerCase: 240,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.00\"",
    height: "2.50\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  },
  {
    id: "RCV-13",
    name: "13 DRAM Reversible Cap Vials",
    capacity: "13 DRAMS (1.63 OZ, 48 ML)",
    unitsPerCase: 200,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "2.75\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  },
  {
    id: "RCV-16",
    name: "16 DRAM Reversible Cap Vials",
    capacity: "16 DRAMS (2.0 OZ, 58 ML)",
    unitsPerCase: 160,
    grossWeight: "3.1 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "3.25\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  },
  {
    id: "RCV-20",
    name: "20 DRAM Reversible Cap Vials",
    capacity: "20 DRAMS (2.5 OZ, 73 ML)",
    unitsPerCase: 140,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.77\"",
    height: "3.0\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  },
  {
    id: "RCV-30",
    name: "30 DRAM Reversible Cap Vials",
    capacity: "30 DRAMS (3.75 OZ, 110 ML)",
    unitsPerCase: 110,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "3.5\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  },
  {
    id: "RCV-40",
    name: "40 DRAM Reversible Cap Vials",
    capacity: "40 DRAMS (5.0 OZ, 146 ML)",
    unitsPerCase: 95,
    grossWeight: "3.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "4.25\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  },
  {
    id: "RCV-60",
    name: "60 DRAM Reversible Cap Vials",
    capacity: "60 DRAMS (7.5 OZ, 220 ML)",
    unitsPerCase: 78,
    grossWeight: "4.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Reversible child resistant", "Push down & turn", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "5.31\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png"
  }
];

// Pop Top Vials data
const popTopVials = [
  {
    id: "PT-13",
    name: "13 DRAM Pop Top Vials",
    capacity: "13 DRAMS (1.63 OZ, 48 ML)",
    unitsPerCase: 200,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Food grade PP material", "Transparent or opaque options", "Pop-top closure", "Easy access"],
    colors: ["Transparent", "Blue", "Opaque", "Custom colors available"],
    diameter: "1.50\"",
    height: "2.75\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/mx2zldoc_image.png"
  },
  {
    id: "PT-19",
    name: "19 DRAM Pop Top Vials",
    capacity: "19 DRAMS (2.38 OZ, 70 ML)",
    unitsPerCase: 150,
    grossWeight: "3.4 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Food grade PP material", "Transparent or opaque options", "Pop-top closure", "Easy access"],
    colors: ["Transparent", "Blue", "Opaque", "Custom colors available"],
    diameter: "1.77\"",
    height: "3.25\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/mx2zldoc_image.png"
  },
  {
    id: "PT-30",
    name: "30 DRAM Pop Top Vials",
    capacity: "30 DRAMS (3.75 OZ, 110 ML)",
    unitsPerCase: 110,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Food grade PP material", "Transparent or opaque options", "Pop-top closure", "Easy access"],
    colors: ["Transparent", "Blue", "Opaque", "Custom colors available"],
    diameter: "1.93\"",
    height: "3.5\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/mx2zldoc_image.png"
  },
  {
    id: "PT-60",
    name: "60 DRAM Pop Top Vials",
    capacity: "60 DRAMS (7.5 OZ, 220 ML)",
    unitsPerCase: 78,
    grossWeight: "4.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Food grade PP material", "Transparent or opaque options", "Pop-top closure", "Easy access"],
    colors: ["Transparent", "Blue", "Opaque", "Custom colors available"],
    diameter: "1.93\"",
    height: "5.31\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/mx2zldoc_image.png"
  }
];

// Robotic Vials data
const roboticVials = [
  {
    id: "RB-08",
    name: "8 DRAM Robotic Vials",
    capacity: "8 DRAMS (1.0 OZ, 29 ML)",
    unitsPerCase: 240,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Compatible with child-resistant caps", "Compatible with snap caps", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.00\"",
    height: "2.50\"",
    category: "Multi-Compatible",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/zjo7wkut_image.png"
  },
  {
    id: "RB-13",
    name: "13 DRAM Robotic Vials",
    capacity: "13 DRAMS (1.63 OZ, 48 ML)",
    unitsPerCase: 200,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Compatible with child-resistant caps", "Compatible with snap caps", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "2.75\"",
    category: "Multi-Compatible",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/zjo7wkut_image.png"
  },
  {
    id: "RB-16",
    name: "16 DRAM Robotic Vials",
    capacity: "16 DRAMS (2.0 OZ, 58 ML)",
    unitsPerCase: 160,
    grossWeight: "3.1 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Compatible with child-resistant caps", "Compatible with snap caps", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "3.25\"",
    category: "Multi-Compatible",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/zjo7wkut_image.png"
  },
  {
    id: "RB-20",
    name: "20 DRAM Robotic Vials",
    capacity: "20 DRAMS (2.5 OZ, 73 ML)",
    unitsPerCase: 140,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Compatible with child-resistant caps", "Compatible with snap caps", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.77\"",
    height: "3.0\"",
    category: "Multi-Compatible",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/zjo7wkut_image.png"
  },
  {
    id: "RB-30",
    name: "30 DRAM Robotic Vials",
    capacity: "30 DRAMS (3.75 OZ, 110 ML)",
    unitsPerCase: 110,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Compatible with child-resistant caps", "Compatible with snap caps", "Screen printing available", "Cap customization"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.93\"",
    height: "3.5\"",
    category: "Multi-Compatible",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/zjo7wkut_image.png"
  }
];

// Oval Bottles data
const ovalBottles = [
  {
    id: "OB-30",
    name: "30ml Oval Bottles",
    capacity: "30 ML (1.0 OZ)",
    unitsPerCase: 200,
    grossWeight: "2.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant caps included", "Oval shape design", "Professional appearance", "Secure closure"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.5\"",
    height: "3.2\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/sky9hvzk_image.png"
  },
  {
    id: "OB-60",
    name: "60ml Oval Bottles",
    capacity: "60 ML (2.0 OZ)",
    unitsPerCase: 150,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant caps included", "Oval shape design", "Professional appearance", "Secure closure"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "1.8\"",
    height: "4.1\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/sky9hvzk_image.png"
  },
  {
    id: "OB-100",
    name: "100ml Oval Bottles",
    capacity: "100 ML (3.4 OZ)",
    unitsPerCase: 120,
    grossWeight: "3.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant caps included", "Oval shape design", "Professional appearance", "Secure closure"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "2.1\"",
    height: "4.8\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/sky9hvzk_image.png"
  },
  {
    id: "OB-150",
    name: "150ml Oval Bottles",
    capacity: "150 ML (5.1 OZ)",
    unitsPerCase: 100,
    grossWeight: "4.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Child-resistant caps included", "Oval shape design", "Professional appearance", "Secure closure"],
    colors: ["Amber", "Blue", "Clear", "Custom colors available"],
    diameter: "2.4\"",
    height: "5.5\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/sky9hvzk_image.png"
  }
];

// Dropper Bottles data
const dropperBottles = [
  {
    id: "DB-15",
    name: "15ml Dropper Bottles",
    capacity: "15 ML (0.5 OZ)",
    unitsPerCase: 300,
    grossWeight: "2.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Amber plastic construction", "Plastic pipette included", "Rubber bulb", "NOT child resistant"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "0.9\"",
    height: "2.8\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/1jce18qr_image.png"
  },
  {
    id: "DB-30",
    name: "30ml Dropper Bottles",
    capacity: "30 ML (1.0 OZ)",
    unitsPerCase: 200,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Amber plastic construction", "Plastic pipette included", "Rubber bulb", "NOT child resistant"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.2\"",
    height: "3.5\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/1jce18qr_image.png"
  },
  {
    id: "DB-60",
    name: "60ml Dropper Bottles",
    capacity: "60 ML (2.0 OZ)",
    unitsPerCase: 150,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Amber plastic construction", "Plastic pipette included", "Rubber bulb", "NOT child resistant"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.5\"",
    height: "4.2\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/1jce18qr_image.png"
  }
];

// Yorker Bottles data
const yorkerBottles = [
  {
    id: "YB-7.5",
    name: "7.5ml Yorker Bottles",
    capacity: "7.5 ML (0.25 OZ)",
    unitsPerCase: 400,
    grossWeight: "2.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Long tip design", "Precise dispensing", "With or without graduation", "Professional appearance"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "0.8\"",
    height: "3.1\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png"
  },
  {
    id: "YB-15",
    name: "15ml Yorker Bottles",
    capacity: "15 ML (0.5 OZ)",
    unitsPerCase: 300,
    grossWeight: "2.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Long tip design", "Precise dispensing", "With or without graduation", "Professional appearance"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "0.9\"",
    height: "3.8\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png"
  },
  {
    id: "YB-30",
    name: "30ml Yorker Bottles",
    capacity: "30 ML (1.0 OZ)",
    unitsPerCase: 200,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Long tip design", "Precise dispensing", "With or without graduation", "Professional appearance"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "1.2\"",
    height: "4.5\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png"
  },
  {
    id: "YB-60",
    name: "60ml Yorker Bottles",
    capacity: "60 ML (2.0 OZ)",
    unitsPerCase: 150,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Long tip design", "Precise dispensing", "With or without graduation", "Professional appearance"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "1.5\"",
    height: "5.2\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png"
  },
  {
    id: "YB-120",
    name: "120ml Yorker Bottles",
    capacity: "120 ML (4.0 OZ)",
    unitsPerCase: 100,
    grossWeight: "4.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Long tip design", "Precise dispensing", "With or without graduation", "Professional appearance"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "1.8\"",
    height: "6.1\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png"
  },
  {
    id: "YB-240",
    name: "240ml Yorker Bottles",
    capacity: "240 ML (8.0 OZ)",
    unitsPerCase: 80,
    grossWeight: "4.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Long tip design", "Precise dispensing", "With or without graduation", "Professional appearance"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "2.1\"",
    height: "7.8\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png"
  },
  {
    id: "YB-480",
    name: "480ml Yorker Bottles",
    capacity: "480 ML (16.0 OZ)",
    unitsPerCase: 50,
    grossWeight: "5.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Long tip design", "Precise dispensing", "With or without graduation", "Professional appearance"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "2.5\"",
    height: "9.2\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png"
  }
];

// Ointment Jars data
const ointmentJars = [
  {
    id: "OJ-30",
    name: "1oz (30ml) Ointment Jars",
    capacity: "1 OZ (30 ML)",
    unitsPerCase: 200,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Plastic construction", "Screw top closure", "Pharmaceutical packing", "Cosmetics compatible"],
    colors: ["Clear", "Amber", "White", "Custom colors available"],
    diameter: "1.8\"",
    height: "1.2\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/0d97x0uw_image.png"
  },
  {
    id: "OJ-60",
    name: "2oz (60ml) Ointment Jars",
    capacity: "2 OZ (60 ML)",
    unitsPerCase: 150,
    grossWeight: "3.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Plastic construction", "Screw top closure", "Pharmaceutical packing", "Cosmetics compatible"],
    colors: ["Clear", "Amber", "White", "Custom colors available"],
    diameter: "2.2\"",
    height: "1.4\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/0d97x0uw_image.png"
  },
  {
    id: "OJ-120",
    name: "4oz (120ml) Ointment Jars",
    capacity: "4 OZ (120 ML)",
    unitsPerCase: 100,
    grossWeight: "4.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Plastic construction", "Screw top closure", "Pharmaceutical packing", "Cosmetics compatible"],
    colors: ["Clear", "Amber", "White", "Custom colors available"],
    diameter: "2.8\"",
    height: "1.8\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/0d97x0uw_image.png"
  },
  {
    id: "OJ-240",
    name: "8oz (240ml) Ointment Jars",
    capacity: "8 OZ (240 ML)",
    unitsPerCase: 80,
    grossWeight: "4.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Plastic construction", "Screw top closure", "Pharmaceutical packing", "Cosmetics compatible"],
    colors: ["Clear", "Amber", "White", "Custom colors available"],
    diameter: "3.2\"",
    height: "2.1\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/0d97x0uw_image.png"
  },
  {
    id: "OJ-480",
    name: "16oz (480ml) Ointment Jars",
    capacity: "16 OZ (480 ML)",
    unitsPerCase: 50,
    grossWeight: "5.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Plastic construction", "Screw top closure", "Pharmaceutical packing", "Cosmetics compatible"],
    colors: ["Clear", "Amber", "White", "Custom colors available"],
    diameter: "3.8\"",
    height: "2.5\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/0d97x0uw_image.png"
  }
];

// Specimen Containers data
const specimenContainers = [
  {
    id: "SP-30",
    name: "30ml Specimen Containers",
    capacity: "30 ML (1.0 OZ)",
    unitsPerCase: 250,
    grossWeight: "3.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Individual packaging", "Sterile available", "Disposable medical container", "Secure closure"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "1.6\"",
    height: "2.2\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/c5npc2hs_image.png"
  },
  {
    id: "SP-60",
    name: "60ml Specimen Containers",
    capacity: "60 ML (2.0 OZ)",
    unitsPerCase: 200,
    grossWeight: "3.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Individual packaging", "Sterile available", "Disposable medical container", "Secure closure"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "2.0\"",
    height: "2.8\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/c5npc2hs_image.png"
  },
  {
    id: "SP-120",
    name: "120ml Specimen Containers",
    capacity: "120 ML (4.0 OZ)",
    unitsPerCase: 150,
    grossWeight: "4.0 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Individual packaging", "Sterile available", "Disposable medical container", "Secure closure"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "2.4\"",
    height: "3.5\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/c5npc2hs_image.png"
  }
];

// Laboratory Accessories data
const labAccessories = [
  {
    id: "LA-MC",
    name: "Measuring Cups",
    capacity: "3 ML - 62 ML Range",
    unitsPerCase: 500,
    grossWeight: "2.5 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Graduated measurements", "Multiple sizes", "Laboratory grade", "Clear markings"],
    colors: ["Clear", "Natural", "Custom colors available"],
    diameter: "Variable",
    height: "Variable",
    category: "Laboratory Equipment",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/pclqwpjt_image.png"
  },
  {
    id: "LA-VFK",
    name: "Veterinary Fecal Test Kits",
    capacity: "Complete Kit",
    unitsPerCase: 100,
    grossWeight: "3.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["Sterile containers", "Individual packaging", "Veterinary grade", "Professional testing"],
    colors: ["Clear with colored caps", "Standard medical colors"],
    diameter: "1.2\"",
    height: "4.5\"",
    category: "Veterinary Supplies",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/pclqwpjt_image.png"
  },
  {
    id: "LA-PRT",
    name: "116mm Child Resistant Pre Roll Tubes",
    capacity: "Variable Volume",
    unitsPerCase: 200,
    grossWeight: "3.2 KG",
    cartonMeas: "33*30*38 CM",
    features: ["116mm length", "Child resistant", "Pre roll storage", "Secure closure"],
    colors: ["Clear", "Amber", "Custom colors available"],
    diameter: "0.8\"",
    height: "4.6\"",
    category: "Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/pclqwpjt_image.png"
  },
  {
    id: "LA-MT",
    name: "Milking Tubes",
    capacity: "Variable Size",
    unitsPerCase: 300,
    grossWeight: "2.8 KG",
    cartonMeas: "33*30*38 CM",
    features: ["63mm length", "3.4mm diameter", "Veterinary grade", "Sterile packaging available"],
    colors: ["Yellow", "Clear", "Custom colors available"],
    diameter: "0.13\"",
    height: "2.5\"",
    category: "Veterinary Supplies",
    image: "https://customer-assets.emergentagent.com/job_meds-storage/artifacts/pclqwpjt_image.png"
  }
];

// PET Tablet Bottles - Round Shape
const petTabletBottlesRound = [
  {
    id: "PTB-20",
    name: "20ML PET Round Tablet Bottle",
    capacity: "20 ML (0.67 OZ)",
    unitsPerCase: "600 units",
    grossWeight: "8.5 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.34\"",
    height: "1.97\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/gnx17njv_image.png"
  },
  {
    id: "PTB-25",
    name: "25ML PET Round Tablet Bottle",
    capacity: "25 ML (0.85 OZ)",
    unitsPerCase: "500 units",
    grossWeight: "9.0 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.42\"",
    height: "2.05\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/gnx17njv_image.png"
  },
  {
    id: "PTB-35",
    name: "35ML PET Round Tablet Bottle",
    capacity: "35 ML (1.18 OZ)",
    unitsPerCase: "450 units",
    grossWeight: "10.0 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.50\"",
    height: "2.36\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/gnx17njv_image.png"
  },
  {
    id: "PTB-50",
    name: "50ML PET Round Tablet Bottle",
    capacity: "50 ML (1.69 OZ)",
    unitsPerCase: "400 units",
    grossWeight: "11.5 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.65\"",
    height: "2.76\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/gnx17njv_image.png"
  },
  {
    id: "PTB-75",
    name: "75ML PET Round Tablet Bottle",
    capacity: "75 ML (2.54 OZ)",
    unitsPerCase: "300 units",
    grossWeight: "12.5 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.85\"",
    height: "3.15\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/rrk12t9o_image.png"
  },
  {
    id: "PTB-100",
    name: "100ML PET Round Tablet Bottle",
    capacity: "100 ML (3.38 OZ)",
    unitsPerCase: "250 units",
    grossWeight: "13.5 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "2.05\"",
    height: "3.54\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/rrk12t9o_image.png"
  },
  {
    id: "PTB-120",
    name: "120ML PET Round Tablet Bottle",
    capacity: "120 ML (4.06 OZ)",
    unitsPerCase: "220 units",
    grossWeight: "14.0 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "2.13\"",
    height: "3.74\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/rrk12t9o_image.png"
  },
  {
    id: "PTB-150",
    name: "150ML PET Round Tablet Bottle",
    capacity: "150 ML (5.07 OZ)",
    unitsPerCase: "180 units",
    grossWeight: "15.0 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "2.28\"",
    height: "4.13\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/rrk12t9o_image.png"
  },
  {
    id: "PTB-200",
    name: "200ML PET Round Tablet Bottle",
    capacity: "200 ML (6.76 OZ)",
    unitsPerCase: "150 units",
    grossWeight: "16.5 KG",
    features: ["PET material", "Round shape", "Tablet storage", "Pharmaceutical grade", "Lightweight"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "2.52\"",
    height: "4.72\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/rrk12t9o_image.png"
  }
];

// PET Tablet Bottles - Rectangle Shape
const petTabletBottlesRect = [
  {
    id: "PTBR-48",
    name: "48ML PET Rectangle Tablet Bottle",
    capacity: "48 ML (1.62 OZ)",
    unitsPerCase: "400 units",
    grossWeight: "11.0 KG",
    features: ["PET material", "Rectangle shape", "Space-efficient", "Pharmaceutical grade", "Stackable design"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.57\"",
    height: "2.95\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png"
  },
  {
    id: "PTBR-65",
    name: "65ML PET Rectangle Tablet Bottle",
    capacity: "65 ML (2.20 OZ)",
    unitsPerCase: "350 units",
    grossWeight: "12.0 KG",
    features: ["PET material", "Rectangle shape", "Space-efficient", "Pharmaceutical grade", "Stackable design"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.69\"",
    height: "3.15\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png"
  },
  {
    id: "PTBR-90",
    name: "90ML PET Rectangle Tablet Bottle",
    capacity: "90 ML (3.04 OZ)",
    unitsPerCase: "300 units",
    grossWeight: "13.5 KG",
    features: ["PET material", "Rectangle shape", "Space-efficient", "Pharmaceutical grade", "Stackable design"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.85\"",
    height: "3.54\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png"
  },
  {
    id: "PTBR-110",
    name: "110ML PET Rectangle Tablet Bottle",
    capacity: "110 ML (3.72 OZ)",
    unitsPerCase: "250 units",
    grossWeight: "14.0 KG",
    features: ["PET material", "Rectangle shape", "Space-efficient", "Pharmaceutical grade", "Stackable design"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "1.97\"",
    height: "3.74\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png"
  },
  {
    id: "PTBR-125",
    name: "125ML PET Rectangle Tablet Bottle",
    capacity: "125 ML (4.23 OZ)",
    unitsPerCase: "220 units",
    grossWeight: "14.5 KG",
    features: ["PET material", "Rectangle shape", "Space-efficient", "Pharmaceutical grade", "Stackable design"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "2.05\"",
    height: "3.94\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png"
  },
  {
    id: "PTBR-165",
    name: "165ML PET Rectangle Tablet Bottle",
    capacity: "165 ML (5.58 OZ)",
    unitsPerCase: "180 units",
    grossWeight: "15.5 KG",
    features: ["PET material", "Rectangle shape", "Space-efficient", "Pharmaceutical grade", "Stackable design"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "2.24\"",
    height: "4.33\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png"
  },
  {
    id: "PTBR-200",
    name: "200ML PET Rectangle Tablet Bottle",
    capacity: "200 ML (6.76 OZ)",
    unitsPerCase: "150 units",
    grossWeight: "16.5 KG",
    features: ["PET material", "Rectangle shape", "Space-efficient", "Pharmaceutical grade", "Stackable design"],
    colors: ["Amber", "Clear", "Custom colors available"],
    diameter: "2.44\"",
    height: "4.72\"",
    category: "Non-Child Resistant",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png"
  }
];

// Disposable Syringes
const disposableSyringes = [
  {
    id: "SYR-1",
    name: "1ML Disposable Syringe",
    capacity: "1 ML",
    unitsPerCase: "100 units",
    grossWeight: "0.8 KG",
    features: ["Medical grade", "Sterile", "Single-use", "Precise measurement", "Luer lock tip"],
    colors: ["Clear/Transparent"],
    diameter: "0.47\"",
    height: "3.15\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/d8avu7cn_image.png"
  },
  {
    id: "SYR-3",
    name: "3ML Disposable Syringe",
    capacity: "3 ML",
    unitsPerCase: "100 units",
    grossWeight: "1.2 KG",
    features: ["Medical grade", "Sterile", "Single-use", "Precise measurement", "Luer lock tip"],
    colors: ["Clear/Transparent"],
    diameter: "0.55\"",
    height: "4.13\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/d8avu7cn_image.png"
  },
  {
    id: "SYR-5",
    name: "5ML Disposable Syringe",
    capacity: "5 ML",
    unitsPerCase: "100 units",
    grossWeight: "1.5 KG",
    features: ["Medical grade", "Sterile", "Single-use", "Precise measurement", "Luer lock tip"],
    colors: ["Clear/Transparent"],
    diameter: "0.63\"",
    height: "4.72\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/d8avu7cn_image.png"
  },
  {
    id: "SYR-10",
    name: "10ML Disposable Syringe",
    capacity: "10 ML",
    unitsPerCase: "100 units",
    grossWeight: "2.0 KG",
    features: ["Medical grade", "Sterile", "Single-use", "Precise measurement", "Luer lock tip"],
    colors: ["Clear/Transparent"],
    diameter: "0.71\"",
    height: "5.51\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/d8avu7cn_image.png"
  }
];

// Measuring Cups
const measuringCups = [
  {
    id: "MC-30",
    name: "30ML Disposable Measuring Cup",
    capacity: "30 ML (1 OZ)",
    unitsPerCase: "500 units",
    grossWeight: "3.5 KG",
    features: ["Disposable", "Clear PP plastic", "Laboratory grade", "Graduated markings", "Stackable"],
    colors: ["Clear/Transparent"],
    diameter: "1.57\"",
    height: "1.38\"",
    category: "Laboratory Equipment",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/3lbo5kce_image.png"
  }
];

// Additional Specimen Containers (Different from SP series)
const specimenContainersExtended = [
  {
    id: "SC-SPEC-20",
    name: "20ML Specimen Container",
    capacity: "20 ML (0.67 OZ)",
    unitsPerCase: "500 units",
    grossWeight: "5.0 KG",
    features: ["Medical grade", "Leak-proof", "Clear plastic", "Sterile option", "Wide mouth"],
    colors: ["Clear/Transparent"],
    diameter: "1.38\"",
    height: "2.17\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/bgayuh76_image.png"
  },
  {
    id: "SC-SPEC-30",
    name: "30ML Specimen Container",
    capacity: "30 ML (1 OZ)",
    unitsPerCase: "400 units",
    grossWeight: "5.5 KG",
    features: ["Medical grade", "Leak-proof", "Clear plastic", "Sterile option", "Wide mouth"],
    colors: ["Clear/Transparent"],
    diameter: "1.57\"",
    height: "2.36\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/bgayuh76_image.png"
  },
  {
    id: "SC-SPEC-50",
    name: "50ML Specimen Container",
    capacity: "50 ML (1.69 OZ)",
    unitsPerCase: "350 units",
    grossWeight: "6.0 KG",
    features: ["Medical grade", "Leak-proof", "Clear plastic", "Sterile option", "Wide mouth"],
    colors: ["Clear/Transparent"],
    diameter: "1.73\"",
    height: "2.76\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/bgayuh76_image.png"
  },
  {
    id: "SC-SPEC-60",
    name: "60ML Specimen Container",
    capacity: "60 ML (2 OZ)",
    unitsPerCase: "300 units",
    grossWeight: "6.5 KG",
    features: ["Medical grade", "Leak-proof", "Clear plastic", "Sterile option", "Wide mouth"],
    colors: ["Clear/Transparent"],
    diameter: "1.85\"",
    height: "2.95\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/bgayuh76_image.png"
  },
  {
    id: "SC-SPEC-80",
    name: "80ML Specimen Container",
    capacity: "80 ML (2.71 OZ)",
    unitsPerCase: "250 units",
    grossWeight: "7.0 KG",
    features: ["Medical grade", "Leak-proof", "Clear plastic", "Sterile option", "Wide mouth"],
    colors: ["Clear/Transparent"],
    diameter: "2.05\"",
    height: "3.15\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/bgayuh76_image.png"
  },
  {
    id: "SC-SPEC-120",
    name: "120ML Specimen Container",
    capacity: "120 ML (4 OZ)",
    unitsPerCase: "200 units",
    grossWeight: "8.0 KG",
    features: ["Medical grade", "Leak-proof", "Clear plastic", "Sterile option", "Wide mouth"],
    colors: ["Clear/Transparent"],
    diameter: "2.36\"",
    height: "3.54\"",
    category: "Medical Grade",
    image: "https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/bgayuh76_image.png"
  }
];

// Mega Tablet Bottles
const megaTabletBottles = [
  {
    id: "MTB-75",
    name: "75ML Mega Tablet Bottle",
    capacity: "75 ML (2.54 OZ)",
    unitsPerCase: "300 units",
    grossWeight: "12.0 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "1.97\"",
    height: "3.35\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-100",
    name: "100ML Mega Tablet Bottle",
    capacity: "100 ML (3.38 OZ)",
    unitsPerCase: "250 units",
    grossWeight: "13.0 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "2.13\"",
    height: "3.74\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-120",
    name: "120ML Mega Tablet Bottle",
    capacity: "120 ML (4.06 OZ)",
    unitsPerCase: "220 units",
    grossWeight: "14.0 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "2.24\"",
    height: "3.94\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-150",
    name: "150ML Mega Tablet Bottle",
    capacity: "150 ML (5.07 OZ)",
    unitsPerCase: "180 units",
    grossWeight: "15.0 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "2.44\"",
    height: "4.33\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-225",
    name: "225ML Mega Tablet Bottle",
    capacity: "225 ML (7.61 OZ)",
    unitsPerCase: "150 units",
    grossWeight: "16.5 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "2.76\"",
    height: "4.92\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-250",
    name: "250ML Mega Tablet Bottle",
    capacity: "250 ML (8.45 OZ)",
    unitsPerCase: "120 units",
    grossWeight: "17.5 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "2.95\"",
    height: "5.31\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-400",
    name: "400ML Mega Tablet Bottle",
    capacity: "400 ML (13.53 OZ)",
    unitsPerCase: "100 units",
    grossWeight: "19.0 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "3.35\"",
    height: "6.10\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-500",
    name: "500ML Mega Tablet Bottle",
    capacity: "500 ML (16.91 OZ)",
    unitsPerCase: "80 units",
    grossWeight: "20.0 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "3.54\"",
    height: "6.69\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  },
  {
    id: "MTB-750",
    name: "750ML Mega Tablet Bottle",
    capacity: "750 ML (25.36 OZ)",
    unitsPerCase: "60 units",
    grossWeight: "22.0 KG",
    features: ["High capacity", "Pharmaceutical grade", "Wide mouth", "Stackable", "Durable plastic"],
    colors: ["Amber", "Clear", "White", "Custom colors available"],
    diameter: "3.94\"",
    height: "7.87\"",
    category: "Pharmaceutical Grade",
    image: "https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png"
  }
];

const allProducts = [...tcvVials, ...snapCapVials, ...crVials, ...rcvVials, ...popTopVials, ...roboticVials, ...ovalBottles, ...dropperBottles, ...yorkerBottles, ...ointmentJars, ...specimenContainers, ...labAccessories, ...petTabletBottlesRound, ...petTabletBottlesRect, ...disposableSyringes, ...measuringCups, ...specimenContainersExtended, ...megaTabletBottles];

// Series metadata for 3-column card layout
const productSeries = [
  {
    id: 'tcv',
    name: 'TCV Series',
    fullName: 'Thumb Click Reversible Cap Vials',
    products: tcvVials,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/dvvkdhbg_image.png',
    category: 'Child Resistant'
  },
  {
    id: 'cr',
    name: 'CR Series',
    fullName: 'Child-Resistant Push & Turn Vials',
    products: crVials,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/almnlitm_image.png',
    category: 'Child Resistant'
  },
  {
    id: 'rcv',
    name: 'RCV Series',
    fullName: 'Reversible Cap Vials',
    products: rcvVials,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/5q782q8p_image.png',
    category: 'Child Resistant'
  },
  {
    id: 'pt',
    name: 'PT Series',
    fullName: 'Pop Top Vials',
    products: popTopVials,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/mx2zldoc_image.png',
    category: 'Non-Child Resistant'
  },
  {
    id: 'rb',
    name: 'RB Series',
    fullName: 'Robotic Vials',
    products: roboticVials,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/zjo7wkut_image.png',
    category: 'Multi-Compatible'
  },
  {
    id: 'ob',
    name: 'OB Series',
    fullName: 'Oval Bottles',
    products: ovalBottles,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/sky9hvzk_image.png',
    category: 'Child Resistant'
  },
  {
    id: 'db',
    name: 'DB Series',
    fullName: 'Dropper Bottles',
    products: dropperBottles,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/1jce18qr_image.png',
    category: 'Non-Child Resistant'
  },
  {
    id: 'yb',
    name: 'YB Series',
    fullName: 'Yorker Bottles',
    products: yorkerBottles,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/e5usldpr_image.png',
    category: 'Medical Grade'
  },
  {
    id: 'oj',
    name: 'OJ Series',
    fullName: 'Ointment Jars',
    products: ointmentJars,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/0d97x0uw_image.png',
    category: 'Medical Grade'
  },
  {
    id: 'sp',
    name: 'SP Series',
    fullName: 'Specimen Containers',
    products: specimenContainers,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/c5npc2hs_image.png',
    category: 'Medical Grade'
  },
  {
    id: 'la',
    name: 'LA Series',
    fullName: 'Laboratory Accessories',
    products: labAccessories,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/pclqwpjt_image.png',
    category: 'Laboratory Equipment'
  },
  {
    id: 'sc',
    name: 'SC Series',
    fullName: 'Snap Cap Vials',
    products: snapCapVials,
    image: 'https://customer-assets.emergentagent.com/job_meds-storage/artifacts/fftxglps_image.png',
    category: 'Non-Child Resistant'
  },
  {
    id: 'ptb',
    name: 'PTB Series',
    fullName: 'PET Round Tablet Bottles',
    products: petTabletBottlesRound,
    image: 'https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/gnx17njv_image.png',
    category: 'Pharmaceutical Grade'
  },
  {
    id: 'ptbr',
    name: 'PTBR Series',
    fullName: 'PET Rectangle Tablet Bottles',
    products: petTabletBottlesRect,
    image: 'https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/un5scmhx_image.png',
    category: 'Pharmaceutical Grade'
  },
  {
    id: 'syr',
    name: 'SYR Series',
    fullName: 'Disposable Syringes',
    products: disposableSyringes,
    image: 'https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/fcg6s1q3_image.png',
    category: 'Medical Grade'
  },
  {
    id: 'mc',
    name: 'MC Series',
    fullName: 'Measuring Cups',
    products: measuringCups,
    image: 'https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/3lbo5kce_image.png',
    category: 'Laboratory Equipment'
  },
  {
    id: 'scspec',
    name: 'SC-SPEC Series',
    fullName: 'Extended Specimen Containers',
    products: specimenContainersExtended,
    image: 'https://customer-assets.emergentagent.com/job_pharmacy-supplies/artifacts/bgayuh76_image.png',
    category: 'Medical Grade'
  },
  {
    id: 'mtb',
    name: 'MTB Series',
    fullName: 'Mega Tablet Bottles',
    products: megaTabletBottles,
    image: 'https://customer-assets.emergentagent.com/job_alberta-pharmacy/artifacts/vh4jb616_image.png',
    category: 'Pharmaceutical Grade'
  }
];

// Compact Grid Tile Section component for testing
const CompactGridSection = ({ title, description, products, selectedVial, setSelectedVial, selectedVialData, quoteForm, setQuoteForm, handleQuoteSubmit }) => {
  const [expandedProduct, setExpandedProduct] = useState(null);

  const handleTileClick = (product) => {
    setSelectedVial(product.id);
    setExpandedProduct(expandedProduct === product.id ? null : product.id);
  };

  const expandedProductData = products.find(p => p.id === expandedProduct);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">
          {title}
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto mb-4">
          {description}
        </p>
        <p className="text-sm text-slate-500">Click any product tile to view details and request quotes</p>
      </div>

      {/* Compact Product Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {products.map(product => (
          <Card 
            key={product.id}
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              expandedProduct === product.id ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'
            }`}
            onClick={() => handleTileClick(product)}
          >
            <CardContent className="p-4 text-center">
              <div className="aspect-square bg-slate-50 rounded-lg mb-3 flex items-center justify-center">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h4 className="font-semibold text-slate-900 text-sm">{product.id}</h4>
              <p className="text-xs text-slate-600 mt-1">{product.capacity}</p>
              <Badge 
                variant={product.category === "Child Resistant" ? "default" : "secondary"}
                className={`mt-2 text-xs ${product.category === "Child Resistant" ? "bg-green-600" : "bg-orange-600"}`}
              >
                {product.category === "Child Resistant" ? "Child Resistant" : product.category}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Expanded Product Details */}
      {expandedProductData && (
        <div className="bg-slate-50 rounded-xl p-6 space-y-6 border-2 border-blue-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-slate-900">
              {expandedProductData.name}
            </h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setExpandedProduct(null)}
            >
              Close
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product Image */}
            <div className="flex justify-center">
              <div className="bg-white rounded-xl shadow-md p-6">
                <img 
                  src={expandedProductData.image} 
                  alt={expandedProductData.name}
                  className="w-48 h-48 object-contain mx-auto"
                />
              </div>
            </div>

            {/* Product Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-slate-700">Capacity:</span>
                  <p className="text-slate-600">{expandedProductData.capacity}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Dimensions:</span>
                  <p className="text-slate-600">Ø{expandedProductData.diameter} × H{expandedProductData.height}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Units/Case:</span>
                  <p className="text-slate-600">{expandedProductData.unitsPerCase} units</p>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Case Weight:</span>
                  <p className="text-slate-600">{expandedProductData.grossWeight}</p>
                </div>
              </div>

              <div>
                <span className="font-medium text-slate-700 block mb-2">Features:</span>
                <div className="flex flex-wrap gap-2">
                  {expandedProductData.features.map(feature => (
                    <Badge key={feature} variant="outline" className="text-xs">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <span className="font-medium text-slate-700 block mb-2">Available Colors:</span>
                <div className="flex flex-wrap gap-2">
                  {expandedProductData.colors.map(color => (
                    <Badge key={color} variant="secondary" className="text-xs">
                      {color}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Safety Warning for Non-Child Resistant */}
              {expandedProductData.category === "Non-Child Resistant" && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-800">
                      CAUTION: NOT CHILD RESISTANT
                    </span>
                  </div>
                </div>
              )}

              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    className="w-full text-white hover:opacity-90 transition-opacity" 
                    style={{ background: 'linear-gradient(135deg, #72429C, #EF842E)' }}
                  >
                    Request Custom Quote - {expandedProductData.id}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Request Custom Quote - {expandedProductData.id}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input 
                          id="name"
                          required
                          value={quoteForm.name}
                          onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input 
                          id="email"
                          type="email"
                          required
                          value={quoteForm.email}
                          onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input 
                          id="phone"
                          value={quoteForm.phone}
                          onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">Pharmacy/Company</Label>
                        <Input 
                          id="company"
                          value={quoteForm.company}
                          onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="quantity">Estimated Quantity *</Label>
                        <Input 
                          id="quantity"
                          required
                          value={quoteForm.quantity}
                          onChange={(e) => setQuoteForm({...quoteForm, quantity: e.target.value})}
                          placeholder="e.g., 500 units, 2 cases"
                        />
                      </div>
                      <div>
                        <Label htmlFor="timeline">Preferred Timeline</Label>
                        <Input 
                          id="timeline"
                          value={quoteForm.timeline}
                          onChange={(e) => setQuoteForm({...quoteForm, timeline: e.target.value})}
                          placeholder="e.g., Within 4 weeks"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="message">Additional Requirements</Label>
                      <Textarea 
                        id="message"
                        rows={4}
                        value={quoteForm.message}
                        onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                        placeholder="Specify colors, special requirements, bulk quantities..."
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                    >
                      Submit Quote Request
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Series Card Component - displays each product series as a card
const SeriesCard = ({ series, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Calculate capacity range
  const capacities = series.products.map(p => {
    const match = p.capacity.match(/(\d+)\s*DRAM/);
    return match ? parseInt(match[1]) : 0;
  }).filter(c => c > 0);
  
  const hasCapacityRange = capacities.length > 0;
  const minCapacity = hasCapacityRange ? Math.min(...capacities) : 0;
  const maxCapacity = hasCapacityRange ? Math.max(...capacities) : 0;

  return (
    <Card 
      className="cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 relative overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(series)}
    >
      <CardContent className="p-6">
        {/* Product Image */}
        <div className="aspect-square bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl mb-4 flex items-center justify-center overflow-hidden">
          <img 
            src={series.image} 
            alt={series.fullName}
            className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-110"
          />
        </div>

        {/* Series Name */}
        <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">
          {series.name}
        </h3>
        
        {/* Full Name */}
        <p className="text-sm text-slate-600 text-center mb-3">
          {series.fullName}
        </p>

        {/* Product Count Badge */}
        <div className="flex justify-center mb-3">
          <Badge 
            className="text-white"
            style={{ background: 'linear-gradient(135deg, #72429C, #EF842E)' }}
          >
            <Package className="w-3 h-3 mr-1" />
            {series.products.length} Products
          </Badge>
        </div>

        {/* Category Badge */}
        <div className="flex justify-center">
          <Badge 
            variant={series.category === "Child Resistant" ? "default" : "secondary"}
            className={`text-xs ${
              series.category === "Child Resistant" 
                ? "bg-green-600" 
                : series.category === "Non-Child Resistant"
                ? "bg-orange-600"
                : "bg-blue-600"
            }`}
          >
            {series.category}
          </Badge>
        </div>

        {/* Hover Overlay - Capacity Range */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-purple-600/95 to-orange-600/95 flex items-center justify-center transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <div className="text-center text-white p-4">
            <Info className="w-8 h-8 mx-auto mb-3" />
            {hasCapacityRange ? (
              <>
                <p className="text-sm font-medium mb-1">Capacity Range</p>
                <p className="text-2xl font-bold">
                  {minCapacity === maxCapacity 
                    ? `${minCapacity} DRAM` 
                    : `${minCapacity}-${maxCapacity} DRAM`}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium mb-1">{series.fullName}</p>
                <p className="text-2xl font-bold">{series.products.length} Products</p>
              </>
            )}
            <p className="text-xs mt-2 opacity-90">Click to view all products</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Series Modal Component - shows all products in a series
const SeriesModal = ({ series, isOpen, onClose, quoteForm, setQuoteForm, handleQuoteSubmit }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'details'

  // Reset to grid when modal closes or series changes
  React.useEffect(() => {
    if (!isOpen) {
      setViewMode('grid');
      setSelectedProduct(null);
    }
  }, [isOpen]);

  if (!isOpen || !series) return null;

  const selectedProductData = series.products.find(p => p.id === selectedProduct);

  const handleProductClick = (productId) => {
    setSelectedProduct(productId);
    setViewMode('details');
  };

  const handleBackToGrid = () => {
    setViewMode('grid');
    setSelectedProduct(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center justify-between">
            <div>
              <span className="text-slate-900">{series.name}</span>
              <span className="text-slate-600 text-lg ml-2">- {series.fullName}</span>
            </div>
            <Badge 
              className="text-white ml-4"
              style={{ background: 'linear-gradient(135deg, #72429C, #EF842E)' }}
            >
              {series.products.length} Products
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {viewMode === 'grid' ? (
          /* Product Grid View */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
            {series.products.map(product => (
              <Card 
                key={product.id}
                className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
                onClick={() => handleProductClick(product.id)}
              >
              <CardContent className="p-4 text-center">
                <div className="aspect-square bg-slate-50 rounded-lg mb-3 flex items-center justify-center">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-20 h-20 object-contain"
                  />
                </div>
                <h4 className="font-semibold text-slate-900 text-sm mb-1">{product.id}</h4>
                <p className="text-xs text-slate-600">{product.capacity}</p>
                <Badge 
                  variant={product.category === "Child Resistant" ? "default" : "secondary"}
                  className={`mt-2 text-xs ${product.category === "Child Resistant" ? "bg-green-600" : "bg-orange-600"}`}
                >
                  {product.category === "Child Resistant" ? "CR" : "NCR"}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
        ) : (
          /* Product Details View - Replaces Grid */
          <div className="mt-6">
            {/* Back Button */}
            <Button 
              variant="outline" 
              onClick={handleBackToGrid}
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to {series.name} Grid
            </Button>

            {selectedProductData && (
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">{selectedProductData.name}</h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Product Image */}
                  <div className="flex justify-center items-start">
                    <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
                      <img 
                        src={selectedProductData.image} 
                        alt={selectedProductData.name}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg">
                        <span className="text-sm font-medium text-slate-500 block mb-1">Capacity</span>
                        <p className="text-lg font-semibold text-slate-900">{selectedProductData.capacity}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <span className="text-sm font-medium text-slate-500 block mb-1">Dimensions</span>
                        <p className="text-lg font-semibold text-slate-900">Ø{selectedProductData.diameter} × H{selectedProductData.height}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <span className="text-sm font-medium text-slate-500 block mb-1">Units/Case</span>
                        <p className="text-lg font-semibold text-slate-900">{selectedProductData.unitsPerCase} units</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <span className="text-sm font-medium text-slate-500 block mb-1">Case Weight</span>
                        <p className="text-lg font-semibold text-slate-900">{selectedProductData.grossWeight}</p>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg">
                      <span className="text-sm font-medium text-slate-500 block mb-3">Features</span>
                      <div className="flex flex-wrap gap-2">
                        {selectedProductData.features.map(feature => (
                          <Badge key={feature} variant="outline" className="text-sm">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg">
                      <span className="text-sm font-medium text-slate-500 block mb-3">Available Colors</span>
                      <div className="flex flex-wrap gap-2">
                        {selectedProductData.colors.map(color => (
                          <Badge key={color} variant="secondary" className="text-sm">
                            {color}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Safety Warning */}
                    {selectedProductData.category === "Non-Child Resistant" && (
                      <div className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="w-5 h-5 text-orange-600" />
                          <span className="text-sm font-bold text-orange-800">
                            CAUTION: NOT CHILD RESISTANT
                          </span>
                        </div>
                      </div>
                    )}

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full text-white hover:opacity-90 transition-opacity text-lg py-6" 
                          style={{ background: 'linear-gradient(135deg, #72429C, #EF842E)' }}
                        >
                          Request Custom Quote - {selectedProductData.id}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Request Custom Quote - {selectedProductData.id}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleQuoteSubmit} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="name">Name *</Label>
                              <Input 
                                id="name"
                                required
                                value={quoteForm.name}
                                onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label htmlFor="email">Email *</Label>
                              <Input 
                                id="email"
                                type="email"
                                required
                                value={quoteForm.email}
                                onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="phone">Phone</Label>
                              <Input 
                                id="phone"
                                value={quoteForm.phone}
                                onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label htmlFor="company">Pharmacy/Company</Label>
                              <Input 
                                id="company"
                                value={quoteForm.company}
                                onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="quantity">Estimated Quantity *</Label>
                              <Input 
                                id="quantity"
                                required
                                value={quoteForm.quantity}
                                onChange={(e) => setQuoteForm({...quoteForm, quantity: e.target.value})}
                                placeholder="e.g., 500 units, 2 cases"
                              />
                            </div>
                            <div>
                              <Label htmlFor="timeline">Desired Timeline</Label>
                              <Input 
                                id="timeline"
                                value={quoteForm.timeline}
                                onChange={(e) => setQuoteForm({...quoteForm, timeline: e.target.value})}
                                placeholder="e.g., 2-3 weeks, ASAP"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="message">Additional Requirements</Label>
                            <Textarea 
                              id="message"
                              value={quoteForm.message}
                              onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                              rows={4}
                              placeholder="Specify colors, special requirements, bulk quantities..."
                            />
                          </div>
                          
                          <Button 
                            type="submit" 
                            className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                          >
                            Submit Quote Request
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Alias for backward compatibility
const VialSection = CompactGridSection;

const AdvanceOrdersPage = () => {
  const [selectedVial, setSelectedVial] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [vialTypeFilter, setVialTypeFilter] = useState('all');
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product: '',
    quantity: '',
    timeline: '',
    message: ''
  });

  // Filter products based on search and filters
  const filteredProducts = useMemo(() => {
    let filtered = allProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          product.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          product.capacity.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
      const matchesVialType = vialTypeFilter === 'all' || 
                             (vialTypeFilter === 'tcv' && product.id.startsWith('TCV')) ||
                             (vialTypeFilter === 'snap' && product.id.startsWith('SC')) ||
                             (vialTypeFilter === 'cr' && product.id.startsWith('CR')) ||
                             (vialTypeFilter === 'rcv' && product.id.startsWith('RCV')) ||
                             (vialTypeFilter === 'pt' && product.id.startsWith('PT')) ||
                             (vialTypeFilter === 'rb' && product.id.startsWith('RB')) ||
                             (vialTypeFilter === 'ob' && product.id.startsWith('OB')) ||
                             (vialTypeFilter === 'db' && product.id.startsWith('DB')) ||
                             (vialTypeFilter === 'yb' && product.id.startsWith('YB')) ||
                             (vialTypeFilter === 'oj' && product.id.startsWith('OJ')) ||
                             (vialTypeFilter === 'sp' && product.id.startsWith('SP')) ||
                             (vialTypeFilter === 'la' && product.id.startsWith('LA')) ||
                             (vialTypeFilter === 'ptb' && product.id.startsWith('PTB')) ||
                             (vialTypeFilter === 'ptbr' && product.id.startsWith('PTBR')) ||
                             (vialTypeFilter === 'syr' && product.id.startsWith('SYR')) ||
                             (vialTypeFilter === 'mc' && product.id.startsWith('MC')) ||
                             (vialTypeFilter === 'scspec' && product.id.startsWith('SC-SPEC')) ||
                             (vialTypeFilter === 'mtb' && product.id.startsWith('MTB'));
      return matchesSearch && matchesCategory && matchesVialType;
    });
    return filtered;
  }, [searchQuery, categoryFilter, vialTypeFilter]);

  const selectedVialData = allProducts.find(vial => vial.id === selectedVial);

  const handleSeriesClick = (series) => {
    setSelectedSeries(series);
    setIsSeriesModalOpen(true);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/quotes/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteForm),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Custom order inquiry submitted! We\'ll contact you within 24 hours with timeline and pricing.');
        console.log('Quote submitted successfully:', data);
      } else {
        throw new Error('Failed to submit quote');
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      toast.error('Failed to submit quote request. Please try again or contact us directly.');
    }
    
    // Reset form
    setQuoteForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      product: '',
      quantity: '',
      timeline: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4 overflow-hidden">
              <Link to="/" className="flex items-center text-purple-600 hover:text-purple-700 flex-shrink-0" style={{ color: '#7C3AED' }}>
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-base font-medium">Back</span>
              </Link>
              <div className="hidden sm:block w-px h-6 bg-slate-300"></div>
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="relative flex-shrink-0">
                  {/* Use icon on mobile, full logo on tablet+ */}
                  <img 
                    src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/euyis7mj_Logo%20-%20APS%20Icon%20-%20Large.png"
                    alt="Alberta Pharmacy Supply Logo"
                    className="block md:hidden w-10 h-10 object-contain"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/tosp0yq2_Logo%20-%20APS%20-%20Large.png"
                    alt="Alberta Pharmacy Supply Logo"
                    className="hidden md:block w-40 h-16 object-contain"
                  />
                </div>
                <div className="min-w-0 md:hidden">
                  <h1 className="text-sm font-bold text-slate-900 truncate">Alberta Pharmacy Supply</h1>
                  <p className="text-xs text-slate-600">Advance Orders</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 flex-shrink-0">
              <Button variant="outline" size="sm" className="hidden sm:flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                {companyInfo.phone}
              </Button>
              <Button variant="outline" size="sm" className="sm:hidden p-2">
                <Phone className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="bg-orange-100 text-orange-800 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex items-center space-x-2 text-xs sm:text-sm">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium">Custom Order Timeline</span>
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-slate-900 mb-3 sm:mb-4 px-2">
              Advance Orders Catalog
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 mb-4 sm:mb-6 max-w-3xl mx-auto px-2">
              Whatever you need, we have it. Just let us know and we can get that sorted for you.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8 max-w-3xl mx-auto">
              <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md border border-slate-200">
                <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 mx-auto mb-2 sm:mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1 sm:mb-2 text-sm sm:text-base">Custom Timeline</h3>
                <p className="text-xs sm:text-sm text-slate-600">Contact us for delivery estimates</p>
              </div>
              <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md border border-slate-200">
                <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mx-auto mb-2 sm:mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1 sm:mb-2 text-sm sm:text-base">Volume Pricing</h3>
                <p className="text-xs sm:text-sm text-slate-600">Competitive rates for larger quantities</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-6 sm:py-8 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by product name, code, or capacity..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 text-sm sm:text-base"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Select value={vialTypeFilter} onValueChange={setVialTypeFilter}>
                <SelectTrigger className="w-full sm:w-56">
                  <SelectValue placeholder="Filter by product type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Product Types</SelectItem>
                  <SelectItem value="tcv">Thumb Click Vials (TCV)</SelectItem>
                  <SelectItem value="cr">Push & Turn Vials (CR)</SelectItem>
                  <SelectItem value="rcv">Reversible Cap Vials (RCV)</SelectItem>
                  <SelectItem value="snap">Snap Cap Vials (SC)</SelectItem>
                  <SelectItem value="pt">Pop Top Vials (PT)</SelectItem>
                  <SelectItem value="rb">Robotic Vials (RB)</SelectItem>
                  <SelectItem value="ob">Oval Bottles (OB)</SelectItem>
                  <SelectItem value="db">Dropper Bottles (DB)</SelectItem>
                  <SelectItem value="yb">Yorker Bottles (YB)</SelectItem>
                  <SelectItem value="oj">Ointment Jars (OJ)</SelectItem>
                  <SelectItem value="sp">Specimen Containers (SP)</SelectItem>
                  <SelectItem value="la">Laboratory Accessories (LA)</SelectItem>
                  <SelectItem value="ptb">PET Round Bottles (PTB)</SelectItem>
                  <SelectItem value="ptbr">PET Rectangle Bottles (PTBR)</SelectItem>
                  <SelectItem value="syr">Disposable Syringes (SYR)</SelectItem>
                  <SelectItem value="mc">Measuring Cups (MC)</SelectItem>
                  <SelectItem value="scspec">Specimen Containers Extended (SC-SPEC)</SelectItem>
                  <SelectItem value="mtb">Mega Tablet Bottles (MTB)</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Child Resistant">Child Resistant</SelectItem>
                  <SelectItem value="Non-Child Resistant">Non-Child Resistant</SelectItem>
                  <SelectItem value="Multi-Compatible">Multi-Compatible</SelectItem>
                  <SelectItem value="Medical Grade">Medical Grade</SelectItem>
                  <SelectItem value="Laboratory Equipment">Laboratory Equipment</SelectItem>
                  <SelectItem value="Veterinary Supplies">Veterinary Supplies</SelectItem>
                  <SelectItem value="Pharmaceutical Grade">Pharmaceutical Grade</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Search Results Summary */}
          {(searchQuery || categoryFilter !== 'all' || vialTypeFilter !== 'all') && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-blue-800 text-sm">
                Found {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} 
                {searchQuery && ` matching "${searchQuery}"`}
                {(categoryFilter !== 'all' || vialTypeFilter !== 'all') && ' with selected filters'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Product Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          
          {/* Show filtered results if searching/filtering */}
          {(searchQuery || categoryFilter !== 'all' || vialTypeFilter !== 'all') ? (
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  Search Results
                </h2>
                <p className="text-slate-600">
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
                </p>
              </div>
              
              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducts.map(product => (
                    <Card key={product.id} className="hover:shadow-lg transition-all duration-300">
                      <CardHeader className="pb-4">
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant={product.category === "Child Resistant" ? "default" : product.category === "Multi-Compatible" ? "secondary" : "secondary"} 
                                 className={product.category === "Child Resistant" ? "bg-green-600" : 
                                           product.category === "Multi-Compatible" ? "bg-blue-600" : 
                                           product.category === "Medical Grade" ? "bg-purple-600" :
                                           product.category === "Laboratory Equipment" ? "bg-indigo-600" :
                                           product.category === "Veterinary Supplies" ? "bg-teal-600" : "bg-orange-600"}>
                            {product.category}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {product.id.startsWith('TCV') ? 'Thumb Click' : 
                             product.id.startsWith('SC') ? 'Snap Cap' : 
                             product.id.startsWith('CR') ? 'Push & Turn' : 
                             product.id.startsWith('RCV') ? 'Reversible Cap' :
                             product.id.startsWith('PT') ? 'Pop Top' :
                             product.id.startsWith('RB') ? 'Robotic' :
                             product.id.startsWith('OB') ? 'Oval Bottle' :
                             product.id.startsWith('DB') ? 'Dropper' : 
                             product.id.startsWith('YB') ? 'Yorker' :
                             product.id.startsWith('OJ') ? 'Ointment Jar' :
                             product.id.startsWith('SP') ? 'Specimen' : 'Lab Accessory'}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg">{product.name}</CardTitle>
                        <p className="text-sm text-slate-600">{product.capacity}</p>
                      </CardHeader>
                      
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex justify-center">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-32 h-32 object-contain"
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="font-medium text-slate-700">Units/Case:</span>
                              <p className="text-slate-600">{product.unitsPerCase}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Dimensions:</span>
                              <p className="text-slate-600">Ø{product.diameter} × H{product.height}</p>
                            </div>
                          </div>
                          
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                className="w-full text-white hover:opacity-90 transition-opacity" 
                                style={{ background: 'linear-gradient(135deg, #72429C, #EF842E)' }}
                              >
                                Select & Quote
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>Request Custom Quote - {product.id}</DialogTitle>
                              </DialogHeader>
                              <form onSubmit={handleQuoteSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <Label htmlFor="name">Name *</Label>
                                    <Input 
                                      id="name"
                                      required
                                      value={quoteForm.name}
                                      onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="email">Email *</Label>
                                    <Input 
                                      id="email"
                                      type="email"
                                      required
                                      value={quoteForm.email}
                                      onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                                    />
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <Label htmlFor="phone">Phone</Label>
                                    <Input 
                                      id="phone"
                                      value={quoteForm.phone}
                                      onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="company">Pharmacy/Company</Label>
                                    <Input 
                                      id="company"
                                      value={quoteForm.company}
                                      onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})}
                                    />
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <Label htmlFor="quantity">Estimated Quantity *</Label>
                                    <Input 
                                      id="quantity"
                                      required
                                      value={quoteForm.quantity}
                                      onChange={(e) => setQuoteForm({...quoteForm, quantity: e.target.value})}
                                      placeholder="e.g., 500 units, 2 cases"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="timeline">Desired Timeline</Label>
                                    <Input 
                                      id="timeline"
                                      value={quoteForm.timeline}
                                      onChange={(e) => setQuoteForm({...quoteForm, timeline: e.target.value})}
                                      placeholder="e.g., 2-3 weeks, ASAP"
                                    />
                                  </div>
                                </div>

                                <div>
                                  <Label htmlFor="message">Additional Requirements</Label>
                                  <Textarea 
                                    id="message"
                                    value={quoteForm.message}
                                    onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                                    rows={4}
                                    placeholder="Specify colors, special requirements, bulk quantities..."
                                  />
                                </div>
                                
                                <Button 
                                  type="submit" 
                                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                                >
                                  Submit Quote Request
                                </Button>
                              </form>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-slate-400 mb-4">
                    <Package className="w-16 h-16 mx-auto" />
                  </div>
                  <p className="text-slate-500 text-lg mb-4">No products found matching your criteria</p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchQuery('');
                      setCategoryFilter('all');
                      setVialTypeFilter('all');
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Series Cards - 3 per row */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">
                    Product Series Catalog
                  </h2>
                  <p className="text-slate-600 max-w-2xl mx-auto">
                    Browse our complete range of pharmaceutical storage solutions. Click any series to explore products.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {productSeries.map(series => (
                    <SeriesCard 
                      key={series.id}
                      series={series}
                      onClick={handleSeriesClick}
                    />
                  ))}
                </div>
              </div>

              {/* Series Modal */}
              <SeriesModal 
                series={selectedSeries}
                isOpen={isSeriesModalOpen}
                onClose={() => setIsSeriesModalOpen(false)}
                quoteForm={quoteForm}
                setQuoteForm={setQuoteForm}
                handleQuoteSubmit={handleQuoteSubmit}
              />
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_meds-storage/artifacts/16xboc6r_WhatsApp%20Image%202025-10-14%20at%2021.25.24_e01b08d0.jpg"
                alt="Alberta Pharmacy Supply Logo"
                className="w-12 h-12 object-contain"
              />
            </div>
            <span className="text-xl font-bold">Alberta Pharmacy Supplies</span>
          </div>
          <p className="text-slate-400 mb-2">Your trusted partner for pharmaceutical storage solutions</p>
          <div className="flex justify-center items-center space-x-4 text-sm text-slate-500">
            <span>© 2024 Alberta Pharmacy Supplies</span>
            <span>•</span>
            <span>Licensed pharmaceutical supplier in Alberta, Canada</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdvanceOrdersPage;
